import React, { useState } from 'react';
import api, { clearStoredAuthTokens } from '../../services/axios';
import { redirectToLogin } from '../../utils/authSession';
import { ENDPOINTS } from '../../config/endpoints';

// ─── constants ────────────────────────────────────────────────────────────────
const GRAD   = 'linear-gradient(135deg,#0041C2,#1A5FD4)';
const GRAD2  = 'linear-gradient(90deg,#0041C2,#1A5FD4)';
const BLUE   = '#0041C2';
const ORANGE = '#E85D04';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_NAMES = ['MON','TUE','WED','THU','FRI','SAT','SUN'];

// 09:00 → 17:30 every 30 min
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h <= 17; h++) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 17 && m > 30) break;
      slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    }
  }
  return slots;
})();

function to12h(t) {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2,'0')}${period}`;
}
function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDay(y, m) {
  const d = new Date(y, m, 1).getDay();
  return d === 0 ? 6 : d - 1; // Mon = 0
}
function isWeekend(y, m, d) {
  const day = new Date(y, m, d).getDay();
  return day === 0 || day === 6;
}
function isPast(y, m, d) {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(y, m, d) < t;
}
function isToday(y, m, d) {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
}
function formatDateLong(y, m, d) {
  return new Date(y, m, d).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

// ─── main component ───────────────────────────────────────────────────────────
export default function UnauthorizedPage() {
  const [blockedUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('blocked_user') || 'null') || {}; }
    catch { return {}; }
  });

  const firstName   = blockedUser.firstName || '';
  const lastName    = blockedUser.lastName  || '';
  const email       = blockedUser.email     || '';
  const fullName    = [firstName, lastName].filter(Boolean).join(' ');
  const displayName = fullName || email.split('@')[0] || '';
  const initials    = displayName
    ? displayName.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()
    : email ? email[0].toUpperCase() : '?';

  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selDate,   setSelDate]   = useState(null);
  const [selTime,   setSelTime]   = useState(null);
  const [use24h,    setUse24h]    = useState(false);
  const [note,      setNote]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitError, setSubmitError] = useState('');

  const todayY = today.getFullYear();
  const todayM = today.getMonth();
  const canGoPrev = viewYear > todayY || (viewYear === todayY && viewMonth > todayM);

  const prevMonth = () => {
    if (!canGoPrev) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
    setSelDate(null); setSelTime(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
    setSelDate(null); setSelTime(null);
  };

  const handleDayClick = (d) => {
    if (isPast(viewYear, viewMonth, d) || isWeekend(viewYear, viewMonth, d)) return;
    setSelDate({ y: viewYear, m: viewMonth, d });
    setSelTime(null);
    setSubmitError('');
  };

  const handleSubmit = async () => {
    if (!selDate || !selTime) return;
    setSubmitting(true);
    setSubmitError('');
    const dateStr = formatDateLong(selDate.y, selDate.m, selDate.d);
    const timeStr = use24h ? selTime : to12h(selTime);
    try {
      const res = await fetch(ENDPOINTS.AUTH.REQUEST_ACCESS, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: fullName || displayName, date: dateStr, time: timeStr, note }),
      });
      if (!res.ok) throw new Error('Server error');
      setSubmitted(true);
    } catch {
      setSubmitError('Could not submit your request. Please try again or reach out directly to your administrator.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try { sessionStorage.removeItem('blocked_user'); } catch (_) {}
    try { await api.post('/auth/logout'); } catch (_) {}
    finally { clearStoredAuthTokens(); redirectToLogin(); }
  };

  // Calendar grid cells: leading nulls + day numbers
  const totalDays = getDaysInMonth(viewYear, viewMonth);
  const firstDay  = getFirstDay(viewYear, viewMonth);
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans','Inter',sans-serif", background: '#F2F4F8' }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        .day-btn { border:none; background:transparent; cursor:pointer; width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:500; color:#1F2937; transition:background 0.15s, color 0.15s; }
        .day-btn:hover:not(:disabled) { background:#EEF2FF; color:${BLUE}; }
        .day-btn:disabled { color:#D1D5DB; cursor:default; }
        .day-btn.today { background:${ORANGE}; color:#fff; font-weight:700; }
        .day-btn.selected { background:${BLUE}; color:#fff; font-weight:700; }
        .day-btn.today.selected { background:${BLUE}; }
        .time-btn { border:1px solid #E5E7EB; background:#fff; cursor:pointer; padding:10px 0; border-radius:8px; font-size:13px; font-weight:500; color:#374151; transition:all 0.15s; width:100%; text-align:center; }
        .time-btn:hover { border-color:${BLUE}; color:${BLUE}; background:#EEF2FF; }
        .time-btn.selected { border-color:${BLUE}; background:${BLUE}; color:#fff; }
        .nav-arrow { border:1px solid #E5E7EB; background:#fff; cursor:pointer; width:28px; height:28px; border-radius:6px; display:flex; align-items:center; justify-content:center; transition:background 0.15s; }
        .nav-arrow:hover:not(:disabled) { background:#EEF2FF; }
        .nav-arrow:disabled { opacity:0.35; cursor:default; }
        .submit-btn { background:${BLUE}; color:#fff; border:none; border-radius:10px; padding:12px 28px; font-size:14px; font-weight:600; cursor:pointer; transition:opacity 0.15s; font-family:inherit; }
        .submit-btn:hover:not(:disabled) { opacity:0.88; }
        .submit-btn:disabled { opacity:0.5; cursor:default; }
        .toggle-pill { display:flex; border:1px solid #E5E7EB; border-radius:6px; overflow:hidden; }
        .toggle-pill button { border:none; background:transparent; padding:4px 10px; font-size:11px; font-weight:600; cursor:pointer; transition:background 0.12s, color 0.12s; font-family:inherit; }
        .toggle-pill button.active { background:${BLUE}; color:#fff; }
        .toggle-pill button:not(.active) { color:#6B7280; }
      `}</style>

      {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
      <aside style={{ width: 230, flexShrink: 0, height: '100vh', background: '#0D1117', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: GRAD2 }} />
        <div style={{ height: 52, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: GRAD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#fff', boxShadow: '0 0 20px rgba(0,65,194,0.35)' }}>H</div>
          <div>
            <div style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.14em', textTransform: 'uppercase' }}>feuji</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>ReachOut</div>
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(253,230,138,0.12)', border: '1px solid rgba(253,230,138,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FDE68A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#FDE68A', marginBottom: 4 }}>Access Pending</div>
            <div style={{ fontSize: 11, color: '#6B7280', lineHeight: 1.6 }}>Book a meeting to request access to this workspace.</div>
          </div>
        </div>
        <div style={{ padding: '10px 10px 14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div onClick={handleLogout} title="Sign out"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 9, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: GRAD, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#E5E7EB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName || email || 'Your account'}</div>
              <div style={{ fontSize: 10, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email || 'Pending access'}</div>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </div>
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Topbar */}
        <div style={{ height: 52, background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid #E4E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 28px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6B7280' }}>
            <span>ReachOut</span>
            <span style={{ color: '#C8D0E0' }}>›</span>
            <span style={{ color: '#0D1B3E', fontWeight: 600 }}>Request Access</span>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #D0D7E8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 24px' }}>
          {submitted ? (
            <SuccessView
              displayName={displayName}
              selDate={selDate}
              selTime={selTime}
              use24h={use24h}
              onLogout={handleLogout}
            />
          ) : (
            <div style={{ width: '100%', maxWidth: 900, animation: 'fadeUp 0.35s ease-out both' }}>
              {/* Card header */}
              <div style={{ marginBottom: 24, textAlign: 'center' }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0D1117', margin: '0 0 6px' }}>Request Access</h1>
                <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>Book a quick call and we'll get you set up as soon as possible.</p>
              </div>

              {/* Three-column booking card */}
              <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)', border: '1px solid #E5E7EB', display: 'flex', overflow: 'hidden', minHeight: 420 }}>

                {/* ── Left: host info ── */}
                <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid #F3F4F6', padding: '28px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: GRAD, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', boxShadow: '0 0 16px rgba(0,65,194,0.3)' }}>R</div>
                    <div>
                      <div style={{ fontSize: 10, color: '#9CA3AF', letterSpacing: '0.1em', textTransform: 'uppercase' }}>feuji</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>ReachOut Admin</div>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: 6 }}>Product Walkthrough &amp; Access</div>
                    <p style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.7, margin: 0 }}>
                      Schedule a quick call to request access. We'll review your account and get you onboarded right away.
                    </p>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Duration */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: ORANGE, color: '#fff' }}>15m</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: '#F3F4F6', color: '#6B7280' }}>30m</span>
                      </div>
                    </div>
                    {/* Meet */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                      <span style={{ fontSize: 12, color: '#4B5563' }}>Google Meet</span>
                    </div>
                    {/* Timezone */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      <span style={{ fontSize: 11, color: '#6B7280', wordBreak: 'break-word' }}>{userTz}</span>
                    </div>
                  </div>

                  {/* Requester identity */}
                  {(displayName || email) && (
                    <div style={{ marginTop: 'auto', padding: '10px 12px', background: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB' }}>
                      <div style={{ fontSize: 10, color: '#9CA3AF', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Requesting as</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 2 }}>{displayName || '—'}</div>
                      {email && <div style={{ fontSize: 11, color: '#6B7280', wordBreak: 'break-all' }}>{email}</div>}
                    </div>
                  )}
                </div>

                {/* ── Center: calendar ── */}
                <div style={{ flex: 1, padding: '24px 20px', borderRight: '1px solid #F3F4F6', minWidth: 0 }}>
                  {/* Month navigation */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      {MONTH_NAMES[viewMonth]} <span style={{ color: ORANGE }}>{viewYear}</span>
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="nav-arrow" disabled={!canGoPrev} onClick={prevMonth} aria-label="Previous month">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                      </button>
                      <button className="nav-arrow" onClick={nextMonth} aria-label="Next month">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* Day names */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 6 }}>
                    {DAY_NAMES.map(d => (
                      <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.06em', padding: '0 0 6px' }}>{d}</div>
                    ))}
                  </div>

                  {/* Day grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px 0' }}>
                    {cells.map((d, i) => {
                      if (!d) return <div key={`e-${i}`} />;
                      const disabled = isPast(viewYear, viewMonth, d) || isWeekend(viewYear, viewMonth, d);
                      const todayCell = isToday(viewYear, viewMonth, d);
                      const sel = selDate && selDate.y === viewYear && selDate.m === viewMonth && selDate.d === d;
                      return (
                        <div key={d} style={{ display: 'flex', justifyContent: 'center' }}>
                          <button
                            className={`day-btn${todayCell ? ' today' : ''}${sel ? ' selected' : ''}`}
                            disabled={disabled}
                            onClick={() => handleDayClick(d)}
                          >
                            {d}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Selected date label */}
                  {selDate && (
                    <div style={{ marginTop: 16, fontSize: 12, color: BLUE, fontWeight: 600 }}>
                      {formatDateLong(selDate.y, selDate.m, selDate.d)}
                    </div>
                  )}
                </div>

                {/* ── Right: time slots or confirmation ── */}
                <div style={{ width: 200, flexShrink: 0, padding: '24px 16px', display: 'flex', flexDirection: 'column' }}>
                  {!selDate ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: '#9CA3AF' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      <span style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.5 }}>Select a date<br />to see available times</span>
                    </div>
                  ) : (
                    <>
                      {/* 12h / 24h toggle */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Available times</span>
                        <div className="toggle-pill">
                          <button className={!use24h ? 'active' : ''} onClick={() => setUse24h(false)}>12h</button>
                          <button className={use24h  ? 'active' : ''} onClick={() => setUse24h(true)}>24h</button>
                        </div>
                      </div>

                      {/* Slot list */}
                      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                        {TIME_SLOTS.map(t => (
                          <button
                            key={t}
                            className={`time-btn${selTime === t ? ' selected' : ''}`}
                            onClick={() => { setSelTime(t); setSubmitError(''); }}
                          >
                            {use24h ? t : to12h(t)}
                          </button>
                        ))}
                      </div>

                      {/* Note + submit (visible once time is picked) */}
                      {selTime && (
                        <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 14 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Message (optional)</div>
                          <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Any context to help us prepare…"
                            rows={3}
                            style={{ width: '100%', resize: 'none', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#111827' }}
                          />
                          {submitError && (
                            <div style={{ fontSize: 11, color: '#DC2626', margin: '6px 0', lineHeight: 1.4 }}>{submitError}</div>
                          )}
                          <button
                            className="submit-btn"
                            disabled={submitting}
                            onClick={handleSubmit}
                            style={{ marginTop: 10, width: '100%' }}
                          >
                            {submitting ? 'Sending…' : 'Request Access'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Sign out link */}
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Sign out and try a different account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── success view ─────────────────────────────────────────────────────────────
function SuccessView({ displayName, selDate, selTime, use24h, onLogout }) {
  const dateStr = selDate ? formatDateLong(selDate.y, selDate.m, selDate.d) : '';
  const timeStr = selTime ? (use24h ? selTime : to12h(selTime)) : '';

  return (
    <div style={{ textAlign: 'center', maxWidth: 480, animation: 'fadeUp 0.35s ease-out both' }}>
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#10B981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 0 0 8px #D1FAE5' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>Request Sent!</h2>
      <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.7, margin: '0 0 20px' }}>
        {displayName ? `Hi ${displayName}, your` : 'Your'} access request has been submitted. An admin will review it and reach out shortly.
      </p>
      {dateStr && (
        <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 24px', marginBottom: 24, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{dateStr}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{timeStr}</span>
          </div>
        </div>
      )}
      <button onClick={onLogout} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 24px', fontSize: 13, color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit' }}>
        Sign out
      </button>
    </div>
  );
}
